import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const TAX_RATE_COLORS = {
  0: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  15: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

const getTaxBadgeColor = (rate) => {
  const parsedRate = parseFloat(rate);

  if (parsedRate === 0) return TAX_RATE_COLORS[0];
  if (parsedRate === 15) return TAX_RATE_COLORS[15];

  return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
};

const getProductId = (product) => product?._id || product?.id;

const ProductTable = ({ products, onEdit, onDelete, isDeleting = false }) => {
  if (!Array.isArray(products) || products.length === 0) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Product
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Description
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Customs Code
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Unit Price
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Tax Rate
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {products.map((product, index) => (
              <ProductRow
                key={getProductId(product) || index}
                product={product}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                isDeleting={isDeleting}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ProductRow = ({ product, index, onEdit, onDelete, isDeleting }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const productId = getProductId(product);

  const actions = [
    ...(onEdit
      ? [
          {
            icon: "Edit3",
            label: "Edit Product",
            color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
            onClick: () => onEdit(product),
          },
        ]
      : []),
    {
      icon: "ShoppingCart",
      label: "Record Purchase Order",
      color: "text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20",
      onClick: () =>
        toast("Purchase order recording coming soon.", { icon: "🚧" }),
    },
    {
      icon: "BarChart2",
      label: "Purchase & Sales Details",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: () =>
        toast("Purchase & sales details coming soon.", { icon: "🚧" }),
    },
    ...(onDelete
      ? [
          {
            icon: "Trash2",
            label: "Delete Product",
            color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
            onClick: () => setShowDeleteConfirm(true),
          },
        ]
      : []),
  ];

  const iconColors = [
    "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
    "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300",
    "bg-teal-100 text-teal-600 dark:bg-teal-900/40 dark:text-teal-300",
    "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300",
    "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-300",
  ];

  const iconColor = iconColors[index % iconColors.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor}`}
            >
              <Icon name="Package" size={16} />
            </div>

            <div>
              <div className="font-medium text-foreground leading-tight">
                {product.name}
              </div>

              {product.uniqueCode && (
                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                  #{product.uniqueCode}
                </div>
              )}
            </div>
          </div>
        </td>

        <td className="px-6 py-4">
          <p
            className="text-sm text-foreground max-w-[200px] truncate"
            title={product.description}
          >
            {product.description || (
              <span className="text-muted-foreground italic">
                No description
              </span>
            )}
          </p>
        </td>

        <td className="px-6 py-4">
          {product.customsCode ? (
            <span className="text-sm font-mono text-foreground bg-muted px-2 py-0.5 rounded">
              {product.customsCode}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>

        <td className="px-6 py-4">
          <div className="font-semibold text-foreground">
            SR{" "}
            {parseFloat(product.unitSellingPrice || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </td>

        <td className="px-6 py-4">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${getTaxBadgeColor(
              product.taxRate,
            )}`}
          >
            <Icon name="Percent" size={11} />
            {parseFloat(product.taxRate || 0).toFixed(0)}%
          </span>
        </td>

        <td className="px-6 py-4">
          <div className="flex items-center gap-1">
            {actions.map((action) => (
              <ActionIconButton
                key={action.label}
                icon={action.icon}
                label={action.label}
                colorClass={action.color}
                onClick={action.onClick}
              />
            ))}
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={6}
              className="px-6 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Are you sure you want to delete{" "}
                  <strong>{product.name}</strong>? This cannot be undone.
                </span>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!productId) {
                        toast.error("Could not identify product.");
                        return;
                      }

                      onDelete(productId);
                      setShowDeleteConfirm(false);
                    }}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionIconButton = ({ icon, label, colorClass, onClick }) => (
  <div className="relative group">
    <button
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${colorClass}`}
      title={label}
    >
      <Icon name={icon} size={15} />
    </button>

    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default ProductTable;
